import React, { useState } from 'react';
import { ClipboardDocumentIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

function AdminPaymentTestTools() {
    const [copiedCard, setCopiedCard] = useState(null);

    // İyzico Test Kartları
    const testCards = {
        success: {
            title: 'Başarılı Ödeme',
            description: 'Test için başarılı ödeme yapan kartlar',
            cards: [
                { name: 'Master Card', number: '5528790000000008', cvv: '123', expiry: '12/30', color: 'bg-orange-50 border-orange-200' },
                { name: 'Visa', number: '4766620000000001', cvv: '123', expiry: '12/30', color: 'bg-blue-50 border-blue-200' },
                { name: 'American Express', number: '374427000000003', cvv: '1234', expiry: '12/30', color: 'bg-green-50 border-green-200' }
            ]
        },
        failure: {
            title: 'Başarısız Ödeme',
            description: 'Test için başarısız ödeme senaryoları',
            cards: [
                { name: 'Yetersiz Bakiye', number: '5406670000000009', cvv: '123', expiry: '12/30', errorCode: '51', color: 'bg-red-50 border-red-200' },
                { name: 'Geçersiz CVV', number: '4111111111111129', cvv: '000', expiry: '12/30', errorCode: '82', color: 'bg-red-50 border-red-200' },
                { name: 'Kart Kısıtlı', number: '5406670000000017', cvv: '123', expiry: '12/30', errorCode: '62', color: 'bg-red-50 border-red-200' }
            ]
        },
        threeds: {
            title: '3D Secure Test',
            description: '3D Secure doğrulama testleri',
            cards: [
                { name: '3DS Başarılı', number: '5528790000000008', cvv: '123', expiry: '12/30', note: '3D şifre: secret', color: 'bg-purple-50 border-purple-200' },
                { name: '3DS Başarısız', number: '4603450000000000', cvv: '123', expiry: '12/30', note: 'Hatalı 3D şifre', color: 'bg-purple-50 border-purple-200' }
            ]
        }
    };

    // Test Senaryoları
    const testScenarios = [
        {
            title: '1. Normal Ödeme Akışı',
            steps: [
                'Ürünleri sepete ekle',
                'Checkout sayfasına git',
                'Teslimat adresini gir',
                'Başarılı test kartı kullan',
                'Ödemeyi tamamla',
                'Order success sayfasına yönlen'
            ],
            expected: 'Sipariş oluşturulmalı, payment success logları kaydedilmeli'
        },
        {
            title: '2. Başarısız Ödeme',
            steps: [
                'Checkout\'a kadar normal işlem',
                'Yetersiz bakiye kartı kullan',
                'Ödeme reddedilmeli',
                'Order failed sayfasına yönlen'
            ],
            expected: 'Hata kodu 51, "Yetersiz bakiye" mesajı, failed log kaydı'
        },
        {
            title: '3. 3D Secure Akışı',
            steps: [
                'Checkout\'a kadar normal işlem',
                '3DS test kartı kullan',
                'İyzico 3D popup açılmalı',
                'Şifre gir: secret',
                'Ödeme tamamlanmalı'
            ],
            expected: '3DS doğrulaması başarılı, ödeme tamamlanmalı'
        },
        {
            title: '4. Refund (İade) İşlemi',
            steps: [
                'Başarılı bir ödeme yap',
                'Admin panelden siparişi bul',
                'İade başlat (Refund)',
                'İade nedenini gir',
                'Onayla'
            ],
            expected: 'İade transaction kaydı, payment status refunded'
        },
        {
            title: '5. Callback Test',
            steps: [
                'Ödeme yap',
                'Payment Logs\'da callback kaydını kontrol et',
                'Callback payload\'ı kontrol et'
            ],
            expected: 'Callback verified, raw_payload ve parsed_payload dolu'
        }
    ];

    // Kontrol Listesi
    const checklist = [
        { category: 'Backend', items: [
            'İyzico API keys doğru mu?',
            'Callback URL production URL\'e ayarlı mı?',
            'Database connections çalışıyor mu?',
            'Payment provider aktif mi?'
        ]},
        { category: 'Frontend', items: [
            'API_BASE_URL production URL\'e işaret ediyor mu?',
            'Checkout sayfası çalışıyor mu?',
            'Order success/failed sayfaları var mı?'
        ]},
        { category: 'Veritabanı', items: [
            'payment_transactions tablosu var mı?',
            'request_payload ve response_payload kolonları var mı?',
            'Foreign key ilişkileri doğru mu?',
            'İndeksler oluşturulmuş mu?'
        ]},
        { category: 'Logs', items: [
            'Payment logs kaydediliyor mu?',
            'Error codes loglanıyor mu?',
            'Context bilgileri tam mı?',
            'Payment callbacks kaydediliyor mu?'
        ]}
    ];

    const copyToClipboard = (text, cardName) => {
        navigator.clipboard.writeText(text);
        setCopiedCard(cardName);
        setTimeout(() => setCopiedCard(null), 2000);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">Payment Test Tools</h1>

            {/* Test Mode Banner */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Test Mode Aktif</h3>
                        <p className="text-sm text-yellow-700 mt-1">
                            Bu kartlar sadece sandbox/test ortamında çalışır. Production'da gerçek kart kullanılmalıdır.
                        </p>
                    </div>
                </div>
            </div>

            {/* Test Kartları */}
            <div className="space-y-6 mb-8">
                <h2 className="text-2xl font-bold">Test Kartları</h2>
                
                {Object.entries(testCards).map(([key, section]) => (
                    <div key={key} className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-xl font-semibold mb-2">{section.title}</h3>
                        <p className="text-gray-600 mb-4">{section.description}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {section.cards.map((card, idx) => (
                                <div key={idx} className={`border-2 rounded-lg p-4 ${card.color}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <h4 className="font-semibold text-gray-900">{card.name}</h4>
                                        {card.errorCode && (
                                            <span className="px-2 py-1 text-xs font-semibold rounded bg-red-600 text-white">
                                                Error {card.errorCode}
                                            </span>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">Kart No:</span>
                                            <div className="flex items-center gap-2">
                                                <code className="font-mono">{card.number}</code>
                                                <button
                                                    onClick={() => copyToClipboard(card.number, `${card.name}-number`)}
                                                    className="text-gray-500 hover:text-gray-700"
                                                    title="Kopyala"
                                                >
                                                    {copiedCard === `${card.name}-number` ? (
                                                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                                    ) : (
                                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">CVV:</span>
                                            <code className="font-mono">{card.cvv}</code>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-gray-600">Son Kullanma:</span>
                                            <code className="font-mono">{card.expiry}</code>
                                        </div>
                                        {card.note && (
                                            <div className="mt-2 pt-2 border-t">
                                                <p className="text-xs text-gray-600">{card.note}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Test Senaryoları */}
            <div className="bg-white rounded-lg shadow p-6 mb-8">
                <h2 className="text-2xl font-bold mb-4">Test Senaryoları</h2>
                <div className="space-y-6">
                    {testScenarios.map((scenario, idx) => (
                        <div key={idx} className="border-l-4 border-blue-500 pl-4">
                            <h3 className="font-semibold text-lg mb-2">{scenario.title}</h3>
                            <div className="mb-3">
                                <p className="text-sm text-gray-600 font-medium mb-2">Adımlar:</p>
                                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                                    {scenario.steps.map((step, stepIdx) => (
                                        <li key={stepIdx}>{step}</li>
                                    ))}
                                </ol>
                            </div>
                            <div className="bg-green-50 p-3 rounded">
                                <p className="text-sm text-green-800">
                                    <strong>Beklenen Sonuç:</strong> {scenario.expected}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Kontrol Listesi */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold mb-4">Deployment Kontrol Listesi</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {checklist.map((section, idx) => (
                        <div key={idx}>
                            <h3 className="font-semibold text-lg mb-3 text-gray-900">{section.category}</h3>
                            <ul className="space-y-2">
                                {section.items.map((item, itemIdx) => (
                                    <li key={itemIdx} className="flex items-start">
                                        <input
                                            type="checkbox"
                                            className="mt-1 mr-2 h-4 w-4 text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* Hızlı Linkler */}
            <div className="mt-8 bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-bold mb-4">Hızlı Erişim</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <a href="/admin/payment-transactions" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        → Payment Transactions
                    </a>
                    <a href="/admin/payment-logs" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        → Payment Logs
                    </a>
                    <a href="/checkout" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        → Checkout (Test)
                    </a>
                    <a href="https://sandbox-merchant.iyzipay.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        → İyzico Dashboard ↗
                    </a>
                </div>
            </div>
        </div>
    );
}

export default AdminPaymentTestTools;
